import MainBlock from "components/DesignSystem/LayoutBlocks/MainBlock";
import React, {
    useEffect,
    useState,
    useContext,
    Fragment,
    useRef,
} from "react";
import { useAuth } from "hooks/Auth";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import {
    apiDel,
    apiGet,
    apiPost,
    apiPut,
    messageFromException,
} from "helpers/Request";
import {
    transform as authorTransformer,
    AuthorObjectSchema,
} from "schemas/Author";
import { useHistory } from "react-router-dom";
import { toast } from "react-hot-toast";
import { AppContext } from "context/App";
import { Formik, Form } from "formik";
import TextInput from "components/FormElements/TextInput";
import CheckBox from "components/FormElements/CheckBox";
import Toolbar, { ToolbarGroup } from "components/Toolbar/Toolbar";
import ToolbarButton from "components/Toolbar/ToolbarButton";
import { SaveIcon, TrashIcon, SupportIcon } from "@heroicons/react/outline";
import { useContextualHelp } from "hooks/Help";
import RichEditor from "components/FormElements/RichEditor";
import MetaField from "./MetaField";
import { slugify } from "Utils";
import PromptIfDirty from "components/FormElements/PromptIfDirty";
import { modal } from "components/HeadlessModal";
import DangerButton from "components/Buttons/DangerButton";
import SecondaryButton from "components/Buttons/SecondaryButton";

export default function AuthorEditScreen(props) {
    const { uuid } = useParams();
    const formRef = useRef();
    const location = useLocation();
    const auth = useAuth();
    const history = useHistory();
    const contextualHelp = useContextualHelp();

    const [author, setAuthor] = useState(
        Object.assign(AuthorObjectSchema, { status: true })
    );
    const [formValues, setFormValues] = useState(
        Object.assign(AuthorObjectSchema, { status: true })
    );
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const { setCurrentNav } = useContext(AppContext);

    const onSubmit = async (values) => {
        const data = Object.assign({}, author, values);

        const resp = await apiGet("/v4/author", auth.publication, {
            keyword: data.slug,
        });
        const authors = authorTransformer(resp).filter((t) => {
            return t.slug === data.slug && t.uuid !== author.uuid;
        });

        if (authors.length) {
            modal.warning({
                title: "Duplicate Authors",
                children: (
                    <Fragment>
                        <div className="mb-4">
                            <strong>
                                It looks like this author might be a duplicate!
                            </strong>{" "}
                            The following authors already exist:
                            <ul className="d-block my-2 list-inside list-disc space-y-0.5">
                                {authors.map((author) => (
                                    <li key={author.uuid}>{author.name}</li>
                                ))}
                            </ul>
                        </div>
                        <div>Do you still want to create this author?</div>
                    </Fragment>
                ),
                buttons: () => (
                    <Fragment>
                        <DangerButton
                            onClick={() => {
                                saveEntry(values).then(() => modal.dismiss());
                            }}
                        >
                            Create Author
                        </DangerButton>
                        <SecondaryButton onClick={modal.dismiss}>
                            Cancel
                        </SecondaryButton>
                    </Fragment>
                ),
            });
        } else {
            saveEntry(values);
        }
    };

    const saveEntry = (values) => {
        const data = Object.assign({}, author, values);

        setSaving(true);
        const id = toast.loading("Saving...");

        const apiAction = author.uuid ? apiPut : apiPost;
        return apiAction(
            "/v4/author/" + (author.uuid ? author.uuid : ""),
            auth.publication,
            data
        )
            .then((resp) => {
                const item = authorTransformer(resp);
                toast.success("Author saved", { id });
                if (!author.uuid) {
                    // was a new item, so redirect
                    history.push("/app/author/" + item.uuid);
                }
                setAuthor(item);
                setSaving(false);
                setTimeout(() => {
                    setLoading(false);
                }, 0);
            })
            .catch((err) => {
                setSaving(false);
                toast.dismiss(id);
                modal.error({
                    title: "Unable to save author",
                    children: messageFromException(err),
                });
            });
    };

    const onDelete = () => {
        modal.warning({
            title: "Delete Author",
            children: (
                <div>
                    <div className="mb-4">
                        <strong>
                            Are you sure you want to delete this author?
                        </strong>
                    </div>
                    <div>
                        <strong>Note:</strong> This action cannot be undone.
                    </div>
                </div>
            ),
            buttons: () => (
                <Fragment>
                    <DangerButton
                        onClick={() => {
                            deleteEntry().then(() => modal.dismiss());
                        }}
                    >
                        Delete Author
                    </DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const deleteEntry = () => {
        const id = toast.loading("Deleting...");
        return apiDel(`/v4/author/${author.uuid}`, auth.publication)
            .then((resp) => {
                toast.success("Author deleted", { id });
                history.push("/app/author");
            })
            .catch((err) => {
                toast.dismiss(id);
                modal.error({
                    title: "Unable to delete author",
                    children: messageFromException(err),
                });
            });
    };

    useEffect(() => {
        setLoading(true);
        setAuthor(AuthorObjectSchema);
        if (!uuid || uuid === "new") {
            setTimeout(() => {
                setLoading(false);
            }, 0);
            return;
        }

        apiGet("/v4/author/" + uuid, auth.publication)
            .then((response) => {
                const item = authorTransformer(response);

                setAuthor(item);
                setTimeout(() => {
                    setLoading(false);
                }, 0);
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
                modal.error({
                    title: "Unable to load author",
                    children: messageFromException(err),
                });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, uuid, auth.publication]);

    useEffect(() => {
        setCurrentNav("author");
    }, [setCurrentNav]);

    useEffect(() => {
        // fixes a bug where the console notes a component updating another, unmounted
        // component. just delay the update until the next tick
        setTimeout(() => {
            setFormValues(author);
        });
    }, [author]);

    // simply watches the loading indicator and resets the form when it changes.
    // this should always be the last `useEffect` in the sequence.
    useEffect(() => {
        setTimeout(() => {
            formRef.current.resetForm({
                values: formValues,
            });
        });
    }, [loading, formValues]);

    return (
        <MainBlock className="z-0">
            <Formik
                innerRef={formRef}
                initialValues={formValues}
                onSubmit={onSubmit}
                enableReinitialize={false}
            >
                {({ setFieldValue, getFieldProps }) => (
                    <Form>
                        <PromptIfDirty />
                        <div className="mx-auto max-w-2xl space-y-4">
                            <Toolbar>
                                <ToolbarGroup>
                                    <ToolbarButton
                                        icon={<SaveIcon />}
                                        type="submit"
                                        disabled={saving}
                                        blocking={saving}
                                    >
                                        Save
                                    </ToolbarButton>
                                    <ToolbarButton
                                        icon={<TrashIcon />}
                                        onClick={onDelete}
                                        disabled={saving || !author.uuid}
                                    >
                                        Delete
                                    </ToolbarButton>
                                </ToolbarGroup>
                                <ToolbarGroup className="ml-auto">
                                    <ToolbarButton
                                        icon={<SupportIcon />}
                                        className="text-green-600"
                                        onClick={() => {
                                            contextualHelp.showContextualHelp(
                                                "/author/edit.html"
                                            );
                                        }}
                                    >
                                        Help
                                    </ToolbarButton>
                                </ToolbarGroup>
                            </Toolbar>
                            {loading ? (
                                <Fragment>
                                    <div className="h-10 w-full animate-pulse bg-gray-200"></div>
                                    <div className="h-10 w-full animate-pulse bg-gray-200"></div>
                                    <div className="h-10 w-full animate-pulse bg-gray-200"></div>
                                </Fragment>
                            ) : (
                                <div className="space-y-4 px-2 sm:px-0">
                                    <TextInput
                                        label="Name"
                                        name="name"
                                        disabled={saving}
                                        onBlur={(e) => {
                                            if (!getFieldProps("slug").value) {
                                                setFieldValue(
                                                    "slug",
                                                    slugify(e.target.value)
                                                );
                                            }
                                        }}
                                    />
                                    <TextInput
                                        label="Slug"
                                        name="slug"
                                        disabled={saving}
                                        onChange={(e) => {
                                            setFieldValue(
                                                "slug",
                                                slugify(e.target.value)
                                            );
                                        }}
                                    />
                                    <CheckBox
                                        label="Status"
                                        name="status"
                                        helpertext="Deactivate authors to keep them from appearing in search results"
                                    />
                                    <TextInput name="email" label="Email" />
                                    <TextInput
                                        name="tagline"
                                        label="Tagline"
                                        helpertext="A short description of the author or author's position"
                                    />
                                    <RichEditor
                                        name="description"
                                        label="Biography"
                                    />
                                    <div className="text-xs text-gray-500">
                                        Author bio information.{" "}
                                        <button
                                            type="button"
                                            className="cursor-help text-green-600 underline"
                                            onClick={() => {
                                                contextualHelp.showContextualHelp(
                                                    "/author/edit.html#updating-author-bios-and-head-shots"
                                                );
                                            }}
                                        >
                                            How to add head shots and social
                                            media.
                                        </button>
                                    </div>
                                    <div>
                                        <MetaField />
                                    </div>
                                </div>
                            )}
                        </div>
                    </Form>
                )}
            </Formik>
        </MainBlock>
    );
}
